import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Bookmark } from 'lucide-react';
import { cn } from '../lib/cn.js';
// English-default microcopy. Was previously pulled through
// `apps/web/src/lib/i18n.ts`'s `STRINGS` — but those entries are DS-
// internal chrome (badge tooltips, retrieval-path labels, aria
// descriptions) that callers don't normally override. Inlining here
// keeps the DS package zero-coupling on the app's i18n store; consumers
// that need locale support can wrap this component.
const STRINGS = {
    open: (id) => `Open source ${id}`,
    langFa: 'Persian source',
    langEn: 'English source',
    retrievalSemanticAria: 'Retrieved via semantic path',
    retrievalHybridAria: 'Retrieved via hybrid path',
    retrievalSemanticTitle: 'Found by semantic vector search only',
    retrievalHybridTitle: 'Found by both graph and semantic retrieval',
    capture: 'Save to investigation',
    thisYear: 'this year',
    yearAgo: '1 year ago',
    yearsAgo: (n) => `${n} years ago`,
};
/**
 * Compact provenance indicator (frontend spec §7).
 * Shows source type + record ID + optional freshness year + relative age +
 * source-language pill (EN / فا). The language pill only appears when the
 * badge has room — i.e. when `compact` is false (default).
 *
 * `retrievalPath` is rendered as a small italic pill ('semantic' or 'hybrid')
 * when the document came from the semantic vector path. Pure graph hits
 * (the legacy default) show no pill.
 *
 * `onCapture` (Phase 2 bridge): when present, renders a tiny bookmark
 * button after the badge that fires the analyst→investigation capture
 * dialog. Opt-in per surface — callers that shouldn't offer capture
 * (e.g. the public brief) simply don't pass `onCapture`.
 */
export function SourceBadge({ id, sourceType, year, sourceLang, retrievalPath, compact = false, className, onClick, onCapture, captureLabel, }) {
    const currentYear = new Date().getFullYear();
    const delta = year != null ? currentYear - year : null;
    const freshness = delta == null
        ? null
        : delta <= 0
            ? STRINGS.thisYear
            : delta === 1
                ? STRINGS.yearAgo
                : STRINGS.yearsAgo(delta);
    const showLang = !compact && sourceLang != null && sourceLang !== 'other';
    // Compact mode (chat-pane) keeps `sourceType + id + year` for
    // provenance at a glance but drops freshness / retrieval / lang
    // pills so the rail doesn't out-weigh the assistant prose. Full
    // info is on hover via the `title` attr (built below).
    const showFreshness = !compact;
    const retrievalLabel = retrievalPath === 'semantic' ? 'semantic' : retrievalPath === 'both' ? 'hybrid' : null;
    const showRetrievalLabel = !compact && retrievalLabel != null;
    // Tooltip carries every field the badge knows about so compact-mode
    // viewers don't lose information the full mode renders inline.
    // Mirrored to `aria-description` so screen readers hear the same
    // provenance — the `title` attr alone isn't reliably announced.
    const titleParts = [];
    if (sourceType)
        titleParts.push(sourceType);
    titleParts.push(id);
    if (year != null)
        titleParts.push(String(year));
    if (freshness)
        titleParts.push(freshness);
    if (retrievalLabel)
        titleParts.push(retrievalLabel);
    if (sourceLang === 'en' || sourceLang === 'fa')
        titleParts.push(sourceLang.toUpperCase());
    const titleText = titleParts.length > 0 ? titleParts.join(' · ') : undefined;
    const interactive = Boolean(onClick);
    // Outer stays a <span> even when interactive — the inner `onCapture`
    // bookmark is a real <button> and nesting a <button> in a <button> is
    // invalid HTML. role+tabIndex+keydown gives us the same a11y contract.
    // Hit target ≥28px when interactive (Apple HIG pointer-target floor).
    function fireOnClick(e) {
        e.preventDefault();
        e.stopPropagation();
        onClick();
    }
    return (_jsxs("span", { role: interactive ? 'button' : undefined, tabIndex: interactive ? 0 : undefined, onClick: interactive ? fireOnClick : undefined, onKeyDown: interactive
            ? (e) => {
                if (e.key === 'Enter' || e.key === ' ')
                    fireOnClick(e);
            }
            : undefined, className: cn('inline-flex items-center gap-1 rounded-xs border px-2 py-0.5 font-mono text-xxs text-[var(--color-ink-muted)]', compact
            ? 'border-[var(--color-border)]/60'
            : 'border-[var(--color-border)] bg-[var(--color-elevated)]', interactive &&
            'min-h-7 cursor-pointer transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-softer)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]', className), title: titleText, "aria-description": titleText, "aria-label": interactive ? STRINGS.open(id) : undefined, children: [sourceType && (_jsx("span", { className: cn('uppercase tracking-wide', 
                // Full-info badges (dossiers, brief readers) get the accent
                // color so the type is the first thing the eye lands on.
                // Compact badges (chat-pane meta rail) drop to ink-muted —
                // a loud accent label here out-weighs the assistant's prose
                // when the response is short, and competes with the meta
                // signal it's supposed to support.
                compact ? 'text-[var(--color-ink-muted)]' : 'text-[var(--color-accent)]'), children: sourceType.replace(/_/g, ' ') })), _jsx("span", { children: id }), year != null && _jsxs("span", { children: ["\u00B7 ", year] }), showFreshness && freshness && (_jsxs("span", { className: "text-[var(--color-ink-muted)] opacity-80", children: ["\u00B7 ", freshness] })), showLang && (_jsx("span", { className: "ms-1 rounded-xs bg-[var(--color-accent-softer)] px-1 text-[var(--color-accent)]", lang: sourceLang, "aria-label": sourceLang === 'fa' ? STRINGS.langFa : STRINGS.langEn, children: sourceLang === 'fa' ? 'فا' : 'EN' })), showRetrievalLabel && (_jsx("span", { className: "ms-1 rounded-xs bg-[var(--color-accent-softer)] px-1 italic text-[var(--color-accent)]", "aria-label": retrievalLabel === 'hybrid'
                    ? STRINGS.retrievalHybridAria
                    : STRINGS.retrievalSemanticAria, title: retrievalLabel === 'hybrid'
                    ? STRINGS.retrievalHybridTitle
                    : STRINGS.retrievalSemanticTitle, children: retrievalLabel })), onCapture && (_jsx("button", { type: "button", onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCapture();
                }, "aria-label": captureLabel ?? STRINGS.capture, title: captureLabel ?? STRINGS.capture, className: "ms-1 inline-flex size-4 items-center justify-center rounded-xs text-[var(--color-ink-muted)] hover:bg-[var(--color-accent-softer)] hover:text-[var(--color-accent)]", children: _jsx(Bookmark, { className: "size-3", "aria-hidden": true }) }))] }));
}
